import {
  all,
  call,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects';

import { post } from '@dmi/shared/utils/request';

import makeSelectMobile from '../../Mobile/selectors';
import { parseReasonCodes } from '../Shared/helpers';
import {
  changeEmailStepOneFailure,
  changeEmailStepOneSuccess,
  changeEmailStepTwoFailure,
  changeEmailStepTwoSuccess,
  changeStep,
  verifyRecaptchaFailure,
  verifyRecaptchaSuccess,
} from './actions';
import {
  CHANGE_EMAIL_ERROR_MESSAGE,
  CHANGE_EMAIL_STEP_ONE,
  CHANGE_EMAIL_STEP_TWO,
  DEFAULT_CHANGE_EMAIL_ERRORS,
  VERIFY_RECAPTCHA,
} from './constants';

export function* changeEmailStepOneSaga({
  baseUrl,
  navigationHandler,
  navigationReplaceHandler,
}, { payload }) {
  const { navigationType, requestBody } = payload;
  const isMobile = yield select(makeSelectMobile('isMobile'));
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  try {
    const endpoint = `${baseUrl}/register/email/stepone`;
    const { error, token } = yield call(post, endpoint, requestBody);
    if (error) {
      const { defaultChangeEmailStepOneErrors } = DEFAULT_CHANGE_EMAIL_ERRORS;
      const { reasons } = error;
      const {
        bannerError,
        serviceError,
        ...validationErrors
      } = parseReasonCodes(reasons, defaultChangeEmailStepOneErrors);
      const hasValidationErrors = Object.values(validationErrors).some((val) => !!val);
      if (bannerError.length || hasValidationErrors) {
        yield put(changeEmailStepOneFailure({ bannerError: bannerError[0], validationErrors }));
      } else {
        throw new Error('serviceError');
      }
    } else {
      yield put(changeEmailStepOneSuccess(token));
      if (isMobile) {
        navigationHandler('ChangeEmailStepTwo');
      } else {
        yield put(changeStep(2));
      }
    }
  } catch (error) {
    yield put(changeEmailStepOneFailure({ error: CHANGE_EMAIL_ERROR_MESSAGE }));
    if (isMobile) {
      const properNavigationHandler = navigationType === 'replace' && isNativeMobile ?
        navigationReplaceHandler :
        navigationHandler;
      properNavigationHandler('ChangeEmailConfirmation');
    } else {
      yield put(changeStep(3));
    }
  }
}

export function* changeEmailStepTwoSaga({
  baseUrl,
  navigationHandler,
  navigationReplaceHandler,
}, { payload }) {
  const { navigationType, requestBody } = payload;
  const isMobile = yield select(makeSelectMobile('isMobile'));
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  try {
    const endpoint = `${baseUrl}/register/email/steptwo`;
    const { error, status } = yield call(post, endpoint, requestBody);
    if (error) {
      const { defaultChangeEmailStepTwoErrors } = DEFAULT_CHANGE_EMAIL_ERRORS;
      const { reasons } = error;
      const {
        bannerError,
        noError,
        serviceError,
        ...validationErrors
      } = parseReasonCodes(reasons, defaultChangeEmailStepTwoErrors);
      const hasValidationErrors = Object.values(validationErrors).some((val) => !!val);
      if (bannerError.length || hasValidationErrors) {
        yield put(changeEmailStepTwoFailure({ bannerError: bannerError[0], validationErrors }));
      } else if (noError) {
        yield put(changeEmailStepTwoSuccess());
      } else {
        throw new Error('serviceError');
      }
    } else {
      yield put(changeEmailStepTwoSuccess(status));
      if (isMobile) {
        const properNavigationHandler = navigationType === 'replace' && isNativeMobile ?
          navigationReplaceHandler :
          navigationHandler;
        properNavigationHandler('ChangeEmailConfirmation');
      } else {
        yield put(changeStep(3));
      }
    }
  } catch (error) {
    yield put(changeEmailStepTwoFailure({ error: CHANGE_EMAIL_ERROR_MESSAGE }));
    if (isMobile) {
      const properNavigationHandler = navigationType === 'replace' && isNativeMobile ?
        navigationReplaceHandler :
        navigationHandler;
      properNavigationHandler('ChangeEmailConfirmation');
    } else {
      yield put(changeStep(3));
    }
  }
}

/**
 * @description Verifies recaptcha response using Google's API
 *  NOTE: This is mostly copy/pasted from packages/shared/redux/Register/Main/saga.js, but:
 *    - switched out some navigation logic to match changeEmailStepOne saga
 *    - updated error handling so that recaptcha error always leads to error confirmation page
 *      (TODO/FIXME: Recaptcha "reset" function doesn't work as expected in RegisterMain version.
 *      Works kinda better if you "bind" the Recaptcha ref/instance to the "reset" fx first, but
 *      still user experience isn't great- no feedback to user.)
 *
 * @return void
 */
export function* verifyRecaptchaSaga({
  baseUrl,
  navigationHandler,
  navigationReplaceHandler,
}, { payload }) {
  const { navigationType, response } = payload;
  const isMobile = yield select(makeSelectMobile('isMobile'));
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  try {
    const endpoint = `${baseUrl}/recaptcha`;
    const { error } = yield call(post, endpoint, { response });
    if (error) {
      throw new Error('serviceError');
    } else {
      yield put(verifyRecaptchaSuccess());
    }
  } catch (error) {
    yield put(verifyRecaptchaFailure({ error: CHANGE_EMAIL_ERROR_MESSAGE }));
    if (isMobile) {
      const properNavigationHandler = navigationType === 'replace' && isNativeMobile ?
        navigationReplaceHandler :
        navigationHandler;
      properNavigationHandler('ChangeEmailConfirmation');
    } else {
      yield put(changeStep(3));
    }
  }
}

export default (options) => function* rootSaga() {
  yield all([
    takeLatest(CHANGE_EMAIL_STEP_ONE, changeEmailStepOneSaga, options),
    takeLatest(CHANGE_EMAIL_STEP_TWO, changeEmailStepTwoSaga, options),
    takeLatest(VERIFY_RECAPTCHA, verifyRecaptchaSaga, options),
  ]);
};
